import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowLeft,
  Play,
  FileText,
  Link as LinkIcon,
  CheckCircle2,
  XCircle,
  Clock,
  Award,
  Eye,
  EyeOff,
  Brain,
  AlertTriangle,
  Loader2,
  FileType,
  FileSpreadsheet,
  Headphones,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Maximize,
  Download,
  Presentation,
} from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
// Import PDF styles - use custom CSS for cross-version compatibility
import '../styles/react-pdf.css';
import { SlideViewer } from '../components/courses/SlideViewer';

// Set up pdf.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CoursePreview = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Helper function to fix content URLs that point to wrong domains
  const getCorrectContentUrl = (url) => {
    if (!url) return url;
    // Extract the path from the URL (everything after /api/...)
    const apiPathMatch = url.match(/\/api\/uploads\/.+$/);
    if (apiPathMatch) {
      // Use the current domain's API
      return `${API_URL.replace('/api', '')}${apiPathMatch[0]}`;
    }
    return url;
  };
  
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeModule, setActiveModule] = useState(0);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizResult, setQuizResult] = useState(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  
  // PDF Viewer state
  const [pdfNumPages, setPdfNumPages] = useState(null);
  const [pdfCurrentPage, setPdfCurrentPage] = useState(1);
  const [pdfScale, setPdfScale] = useState(1.0);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfLastPageReached, setPdfLastPageReached] = useState(false);
  
  // Page timer state - 15 second delay before next page
  const [pageTimerActive, setPageTimerActive] = useState(false);
  const [pageTimeRemaining, setPageTimeRemaining] = useState(15);
  const pageTimerRef = useRef(null);
  
  // Audio player state
  const audioRef = useRef(null);
  const [audioCompleted, setAudioCompleted] = useState(false);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioMaxListenedTime, setAudioMaxListenedTime] = useState(0);
  const [audioPlaying, setAudioPlaying] = useState(false);
  
  // Slide viewer state
  const [slideLastReached, setSlideLastReached] = useState(false);
  
  // Track completed modules in preview
  const [completedModulesPreview, setCompletedModulesPreview] = useState([]);
  
  useEffect(() => {
    fetchCoursePreview();
  }, [courseId]);
  
  const fetchCoursePreview = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/courses/${courseId}/preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCourse(response.data.course);
    } catch (error) {
      toast.error('Failed to load course preview');
      navigate('/course-management');
    } finally {
      setLoading(false);
    }
  };
  
  const handleQuizSubmit = async () => {
    if (!course?.quiz) return;
    
    const questions = course.quiz.questions || [];
    const totalQuestions = questions.length;
    
    // Calculate score
    let correct = 0;
    questions.forEach((q, index) => {
      if (quizAnswers[index] === q.correct_answer) {
        correct++;
      }
    });
    
    const score = Math.round((correct / totalQuestions) * 100);
    
    setSubmittingQuiz(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/courses/${courseId}/preview/quiz`,
        { answers: quizAnswers, score },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuizResult(response.data);
      setQuizSubmitted(true);
      toast.success('Preview quiz completed!');
    } catch (error) {
      toast.error('Failed to submit quiz preview');
    } finally {
      setSubmittingQuiz(false);
    }
  };
  
  const resetQuiz = () => {
    setQuizAnswers({});
    setQuizSubmitted(false);
    setQuizResult(null);
  };
  
  const getModuleIcon = (type) => {
    switch (type) {
      case 'video': return <Play className="w-4 h-4" />;
      case 'text': return <FileText className="w-4 h-4" />;
      case 'link': return <LinkIcon className="w-4 h-4" />;
      case 'pdf': return <FileType className="w-4 h-4" />;
      case 'ppt': return <FileSpreadsheet className="w-4 h-4" />;
      case 'audio': return <Headphones className="w-4 h-4" />;
      case 'slide': return <Presentation className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };
  
  // PDF navigation functions
  const onPdfLoadSuccess = ({ numPages }) => {
    setPdfNumPages(numPages);
    setPdfCurrentPage(1);
    setPdfLoading(false);
    // Start timer for first page
    startPageTimer();
    // If PDF has only 1 page, mark as reached
    if (numPages === 1) {
      setPdfLastPageReached(true);
    }
  };
  
  // Start 15-second timer for current page
  const startPageTimer = () => {
    setPageTimerActive(true);
    setPageTimeRemaining(15);
    
    if (pageTimerRef.current) {
      clearInterval(pageTimerRef.current);
    }
    
    pageTimerRef.current = setInterval(() => {
      setPageTimeRemaining(prev => {
        if (prev <= 1) {
          setPageTimerActive(false);
          clearInterval(pageTimerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
  
  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (pageTimerRef.current) {
        clearInterval(pageTimerRef.current);
      }
    };
  }, []);
  
  const goToPrevPage = () => setPdfCurrentPage(prev => Math.max(prev - 1, 1));
  const goToNextPage = () => {
    const nextPage = Math.min(pdfCurrentPage + 1, pdfNumPages || 1);
    setPdfCurrentPage(nextPage);
    // Start timer for new page
    startPageTimer();
    // Check if reaching last page
    if (nextPage === pdfNumPages) {
      setPdfLastPageReached(true);
    }
  };
  const zoomIn = () => setPdfScale(prev => Math.min(prev + 0.25, 3.0));
  const zoomOut = () => setPdfScale(prev => Math.max(prev - 0.25, 0.5));
  
  // Audio player handlers
  const handleAudioTimeUpdate = () => {
    if (audioRef.current) {
      const currentTime = audioRef.current.currentTime;
      setAudioCurrentTime(currentTime);
      if (currentTime > audioMaxListenedTime) {
        setAudioMaxListenedTime(currentTime);
      }
    }
  };

  const handleAudioSeeking = () => {
    if (audioRef.current) {
      const currentTime = audioRef.current.currentTime;
      if (currentTime > audioMaxListenedTime + 0.5) {
        audioRef.current.currentTime = audioMaxListenedTime;
        toast.warning('Please listen to the entire audio. Forwarding is disabled.');
      }
    }
  };

  const handleAudioLoadedMetadata = () => {
    if (audioRef.current) {
      setAudioDuration(audioRef.current.duration);
    }
  };

  const handleAudioEnded = () => {
    setAudioCompleted(true);
    setAudioPlaying(false);
    toast.success('Audio completed! You can now proceed.');
  };

  const formatAudioTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Check if current module can be marked complete
  const canMarkModuleComplete = () => {
    const currentModule = course?.modules?.[activeModule];
    if (!currentModule) return false;
    
    // For PDF modules, must reach last page
    if (currentModule.type === 'pdf') {
      return pdfLastPageReached;
    }
    
    // For PPT modules (uploaded), allow completion (using Google Docs Viewer iframe - can't track slides)
    // User must self-attest they've viewed all slides
    if (currentModule.type === 'ppt') {
      return true;
    }
    
    // For Slide modules (designed slides), must reach last slide
    if (currentModule.type === 'slide') {
      return slideLastReached;
    }
    
    // For audio modules, must listen to entire audio
    if (currentModule.type === 'audio') {
      return audioCompleted;
    }
    
    // For other modules, always allow
    return true;
  };
  
  // Mark current module as complete (preview mode - local state only)
  const markModuleComplete = () => {
    const currentModule = course?.modules?.[activeModule];
    if (!currentModule || !canMarkModuleComplete()) return;
    
    if (!completedModulesPreview.includes(currentModule.id)) {
      setCompletedModulesPreview(prev => [...prev, currentModule.id]);
    }
    
    // Move to next module or show quiz
    if (activeModule < (course?.modules?.length || 0) - 1) {
      setActiveModule(activeModule + 1);
    } else if (course?.quiz?.questions?.length > 0) {
      setShowQuiz(true);
    }
  };
  
  // Reset PDF, audio, and slide state when module changes
  useEffect(() => {
    setPdfCurrentPage(1);
    setPdfNumPages(null);
    setPdfLoading(true);
    setPdfLastPageReached(false);
    setPageTimerActive(false);
    setPageTimeRemaining(15);
    if (pageTimerRef.current) {
      clearInterval(pageTimerRef.current);
    }
    // Reset audio state
    setAudioCompleted(false);
    setAudioCurrentTime(0);
    setAudioDuration(0);
    setAudioMaxListenedTime(0);
    setAudioPlaying(false);
    // Reset slide state
    setSlideLastReached(false);
  }, [activeModule]);
  
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }
  
  if (!course) {
    return (
      <DashboardLayout>
        <Card className="p-12 text-center">
          <XCircle className="w-16 h-16 mx-auto text-destructive mb-4" />
          <h2 className="text-xl font-semibold mb-2">Course Not Found</h2>
          <p className="text-muted-foreground mb-4">The requested course could not be loaded.</p>
          <Button onClick={() => navigate('/course-management')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Course Management
          </Button>
        </Card>
      </DashboardLayout>
    );
  }
  
  const modules = course.modules || [];
  const currentModule = modules[activeModule];
  const quiz = course.quiz;
  
  return (
    <DashboardLayout>
      <div className="space-y-6 pb-20 lg:pb-0" data-testid="course-preview">
        {/* Preview Banner */}
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-amber-500/20">
                <Eye className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-amber-700 dark:text-amber-400">Preview Mode</h3>
                <p className="text-sm text-amber-600 dark:text-amber-500">
                  You're viewing this course as a learner would see it. Progress and quiz results will NOT be saved.
                </p>
              </div>
              <Button variant="outline" onClick={() => navigate('/course-management')} data-testid="exit-preview-btn">
                <EyeOff className="w-4 h-4 mr-2" />
                Exit Preview
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline">{course.category}</Badge>
              <Badge variant={course.is_published ? 'default' : 'secondary'}>
                {course.is_published ? 'Published' : 'Draft'}
              </Badge>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold">{course.title}</h1>
            <p className="text-muted-foreground mt-2">{course.description}</p>
            <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {course.duration_minutes} min
              </span>
              <span className="flex items-center gap-1">
                <Award className="w-4 h-4" />
                {course.points_reward} points
              </span>
              <span className="flex items-center gap-1">
                <FileText className="w-4 h-4" />
                {modules.length} modules
              </span>
              {quiz && (
                <span className="flex items-center gap-1">
                  <Brain className="w-4 h-4" />
                  Quiz ({quiz.questions?.length || 0} Qs)
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {!showQuiz ? (
              <>
                {/* Module Content */}
                {currentModule ? (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        {getModuleIcon(currentModule.type)}
                        {currentModule.title}
                      </CardTitle>
                      <CardDescription>
                        Module {activeModule + 1} of {modules.length}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {currentModule.type === 'video' && currentModule.video_url && (
                        <div className="aspect-video bg-black rounded-lg overflow-hidden mb-4">
                          {currentModule.video_url.includes('youtube') || currentModule.video_url.includes('youtu.be') ? (
                            <iframe
                              src={currentModule.video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                              className="w-full h-full"
                              allowFullScreen
                              title={currentModule.title}
                            />
                          ) : (
                            <video
                              src={currentModule.video_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${currentModule.video_url}` : currentModule.video_url}
                              controls
                              className="w-full h-full"
                            />
                          )}
                        </div>
                      )}
                      
                      {/* Text content - only show for text type, not link type */}
                      {currentModule.type === 'text' && currentModule.content && (
                        <div className="prose dark:prose-invert max-w-none">
                          <p className="whitespace-pre-wrap">{currentModule.content}</p>
                        </div>
                      )}
                      
                      {/* Link type - show embedded iframe */}
                      {(currentModule.type === 'link' || currentModule.type === 'url') && currentModule.content && (
                        <div className="space-y-4">
                          {/* Embedded iframe for external content */}
                          <div className="relative w-full" style={{ paddingBottom: '75%', height: 0 }}>
                            <iframe
                              src={currentModule.content}
                              className="absolute top-0 left-0 w-full h-full rounded-lg border"
                              style={{ minHeight: '500px' }}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                              allowFullScreen
                              title={currentModule.title}
                            />
                          </div>
                          {/* Fallback button */}
                          <div className="text-center pt-4 border-t">
                            <p className="text-sm text-muted-foreground mb-2">
                              Can't see the content? Open in a new tab:
                            </p>
                            <Button asChild variant="outline" size="sm">
                              <a href={currentModule.content} target="_blank" rel="noopener noreferrer">
                                <LinkIcon className="w-4 h-4 mr-2" />
                                Open External Resource
                              </a>
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      {/* PDF Viewer */}
                      {currentModule.type === 'pdf' && currentModule.content && (
                        <div className="space-y-4">
                          {/* PDF Controls */}
                          <div className="flex items-center justify-between p-3 bg-muted rounded-lg flex-wrap gap-2">
                            <div className="flex items-center gap-2">
                              <FileType className="w-5 h-5 text-primary" />
                              <span className="font-medium">{currentModule.title}</span>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              {/* Page Navigation */}
                              <div className="flex items-center gap-1 bg-background rounded-md px-2 py-1">
                                <Button variant="ghost" size="sm" onClick={goToPrevPage} disabled={pdfCurrentPage <= 1} className="h-7 px-2">
                                  <ChevronLeft className="w-4 h-4" />
                                </Button>
                                <span className="text-sm min-w-[80px] text-center">{pdfCurrentPage} / {pdfNumPages || '...'}</span>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={goToNextPage} 
                                  disabled={pdfCurrentPage >= (pdfNumPages || 1) || pageTimerActive} 
                                  className="h-7 px-2"
                                >
                                  {pageTimerActive ? (
                                    <span className="text-xs">{pageTimeRemaining}s</span>
                                  ) : (
                                    <ChevronRight className="w-4 h-4" />
                                  )}
                                </Button>
                              </div>
                              
                              {/* Zoom Controls */}
                              <div className="flex items-center gap-1 bg-background rounded-md px-2 py-1">
                                <Button variant="ghost" size="sm" onClick={zoomOut} disabled={pdfScale <= 0.5} className="h-7 px-2">
                                  <ZoomOut className="w-4 h-4" />
                                </Button>
                                <span className="text-sm min-w-[50px] text-center">{Math.round(pdfScale * 100)}%</span>
                                <Button variant="ghost" size="sm" onClick={zoomIn} disabled={pdfScale >= 3.0} className="h-7 px-2">
                                  <ZoomIn className="w-4 h-4" />
                                </Button>
                              </div>
                              
                              <Button variant="outline" size="sm" asChild>
                                <a href={getCorrectContentUrl(currentModule.content)} target="_blank" rel="noopener noreferrer">
                                  <Maximize className="w-4 h-4 mr-2" />
                                  Full Screen
                                </a>
                              </Button>
                            </div>
                          </div>
                          
                          {/* PDF Document */}
                          <div className="relative w-full bg-gray-100 dark:bg-gray-900 rounded-lg overflow-auto flex justify-center" style={{ height: '60vh', minHeight: '400px' }}>
                            <Document
                              file={getCorrectContentUrl(currentModule.content)}
                              onLoadSuccess={onPdfLoadSuccess}
                              onLoadError={(error) => {
                                console.error('PDF load error:', error);
                                setPdfLoading(false);
                              }}
                              loading={
                                <div className="flex items-center justify-center h-full">
                                  <div className="text-center">
                                    <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-primary" />
                                    <p className="text-sm text-muted-foreground">Loading PDF...</p>
                                  </div>
                                </div>
                              }
                              error={
                                <div className="flex items-center justify-center h-full">
                                  <div className="text-center p-6">
                                    <AlertTriangle className="w-12 h-12 text-destructive mx-auto mb-3" />
                                    <p className="text-sm text-muted-foreground mb-4">Unable to load PDF.</p>
                                    <Button variant="outline" asChild>
                                      <a href={getCorrectContentUrl(currentModule.content)} target="_blank" rel="noopener noreferrer">
                                        <Maximize className="w-4 h-4 mr-2" />
                                        Open in New Tab
                                      </a>
                                    </Button>
                                  </div>
                                </div>
                              }
                            >
                              <Page pageNumber={pdfCurrentPage} scale={pdfScale} className="shadow-lg" renderTextLayer={true} renderAnnotationLayer={true} />
                            </Document>
                          </div>
                          
                          {/* Bottom Navigation */}
                          <div className="flex items-center justify-center gap-4 py-2">
                            <Button variant="outline" onClick={goToPrevPage} disabled={pdfCurrentPage <= 1}>
                              <ChevronLeft className="w-4 h-4 mr-2" />
                              Previous Page
                            </Button>
                            <span className="text-sm text-muted-foreground">Page {pdfCurrentPage} of {pdfNumPages || '...'}</span>
                            <Button 
                              variant="outline" 
                              onClick={goToNextPage} 
                              disabled={pdfCurrentPage >= (pdfNumPages || 1) || pageTimerActive}
                            >
                              {pageTimerActive ? (
                                <>Wait {pageTimeRemaining}s</>
                              ) : (
                                <>Next Page</>
                              )}
                              <ChevronRight className="w-4 h-4 ml-2" />
                            </Button>
                          </div>
                          
                          {/* Timer indicator */}
                          {pageTimerActive && (
                            <div className="text-center p-2 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
                              <p className="text-sm text-blue-700 dark:text-blue-300">
                                ⏱️ Please spend at least 15 seconds on each page. Time remaining: {pageTimeRemaining}s
                              </p>
                            </div>
                          )}
                          
                          {/* Progress indicator for PDF completion */}
                          {!pdfLastPageReached && pdfNumPages && !pageTimerActive && (
                            <div className="text-center p-3 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg">
                              <p className="text-sm text-amber-700 dark:text-amber-300">
                                📖 Read through all {pdfNumPages} pages to mark this module as complete
                              </p>
                              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                Progress: {pdfCurrentPage} of {pdfNumPages} pages viewed
                              </p>
                            </div>
                          )}
                          
                          {pdfLastPageReached && (
                            <div className="text-center p-3 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg">
                              <p className="text-sm text-green-700 dark:text-green-300 flex items-center justify-center gap-2">
                                <CheckCircle2 className="w-4 h-4" />
                                You've read all pages! You can now proceed.
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* PowerPoint Viewer - Uses Google Docs Viewer for embedded viewing */}
                      {currentModule.type === 'ppt' && currentModule.content && (
                        <div className="space-y-4">
                          {/* PPT Viewer using Microsoft Office Online */}
                          <div 
                            className="w-full bg-white dark:bg-gray-900 rounded-lg overflow-hidden shadow-lg border"
                            style={{ 
                              height: 'calc(100vh - 300px)',
                              minHeight: '450px',
                              maxHeight: '650px'
                            }}
                          >
                            {(() => {
                              const pptUrl = getCorrectContentUrl(currentModule.content);
                              
                              // Build public URL for external viewer access
                              let publicUrl = pptUrl;
                              
                              // Handle both relative and absolute URLs
                              // Extract filename from various URL patterns
                              let filename = null;
                              
                              // Pattern 1: Full URL with /api/uploads/documents/
                              const fullUrlMatch = pptUrl.match(/\/api\/uploads\/documents\/([^?#]+)/);
                              if (fullUrlMatch) {
                                filename = fullUrlMatch[1];
                              }
                              
                              // Pattern 2: Relative /uploads/documents/
                              if (!filename) {
                                const relativeMatch = pptUrl.match(/\/uploads\/documents\/([^?#]+)/);
                                if (relativeMatch) {
                                  filename = relativeMatch[1];
                                }
                              }
                              
                              // If we found a filename, construct the public-docs URL
                              if (filename) {
                                publicUrl = `${window.location.origin}/api/public-docs/${filename}`;
                              } else if (!pptUrl.startsWith('http')) {
                                // Relative URL without known pattern - just prepend origin
                                publicUrl = `${window.location.origin}${pptUrl}`;
                              }
                              
                              const encodedUrl = encodeURIComponent(publicUrl);
                              const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`;
                              
                              return (
                                <iframe
                                  src={viewerUrl}
                                  width="100%"
                                  height="100%"
                                  style={{ border: 'none', display: 'block' }}
                                  title={`PPT Viewer - ${currentModule.title}`}
                                  allowFullScreen
                                  loading="lazy"
                                />
                              );
                            })()}
                          </div>
                          
                          {/* Instructions */}
                          <div className="text-center p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
                            <p className="text-sm text-blue-700 dark:text-blue-300">
                              📊 Use the arrows at the bottom of the presentation to navigate between slides. View all slides before marking complete.
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {/* Audio Player - No Forward/Seek Allowed */}
                      {currentModule.type === 'audio' && currentModule.content && (
                        <div className="space-y-4">
                          <div className="p-8 bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg">
                            <div className="text-center mb-6">
                              <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-4 transition-all ${audioPlaying ? 'bg-primary/30 animate-pulse' : 'bg-primary/20'}`}>
                                <Headphones className="w-12 h-12 text-primary" />
                              </div>
                              <h3 className="text-lg font-medium">{currentModule.title}</h3>
                            </div>
                            
                            {/* Custom Audio Player with Seek Prevention */}
                            <div className="max-w-md mx-auto space-y-4">
                              {/* Hidden HTML5 Audio Element */}
                              <audio 
                                ref={audioRef}
                                src={getCorrectContentUrl(currentModule.content)}
                                onTimeUpdate={handleAudioTimeUpdate}
                                onSeeking={handleAudioSeeking}
                                onLoadedMetadata={handleAudioLoadedMetadata}
                                onEnded={handleAudioEnded}
                                onPlay={() => setAudioPlaying(true)}
                                onPause={() => setAudioPlaying(false)}
                                preload="metadata"
                              />
                              
                              {/* Progress Bar - Visual Only, No Seeking */}
                              <div className="space-y-2">
                                <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                                  <div 
                                    className="absolute left-0 top-0 h-full bg-primary transition-all duration-300"
                                    style={{ width: `${audioDuration > 0 ? (audioCurrentTime / audioDuration) * 100 : 0}%` }}
                                  />
                                </div>
                                <div className="flex justify-between text-xs text-muted-foreground">
                                  <span>{formatAudioTime(audioCurrentTime)}</span>
                                  <span>{formatAudioTime(audioDuration)}</span>
                                </div>
                              </div>
                              
                              {/* Play/Pause Button */}
                              <div className="flex justify-center">
                                <Button
                                  size="lg"
                                  className="rounded-full w-16 h-16"
                                  onClick={() => {
                                    if (audioRef.current) {
                                      if (audioPlaying) {
                                        audioRef.current.pause();
                                      } else {
                                        audioRef.current.play();
                                      }
                                    }
                                  }}
                                >
                                  {audioPlaying ? (
                                    <Play className="w-8 h-8 rotate-0" style={{ display: 'none' }} />
                                  ) : null}
                                  {audioPlaying ? (
                                    <span className="w-8 h-8 flex items-center justify-center">⏸</span>
                                  ) : (
                                    <Play className="w-8 h-8 ml-1" />
                                  )}
                                </Button>
                              </div>
                              
                              {/* Info Message */}
                              <div className="text-center text-xs text-muted-foreground">
                                🔒 Forward/seek is disabled. Please listen to the entire audio.
                              </div>
                            </div>
                          </div>
                          
                          {/* Audio Completion Status */}
                          {!audioCompleted ? (
                            <div className="text-center p-3 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg">
                              <p className="text-sm text-amber-700 dark:text-amber-300">
                                🎧 Listen to the entire audio to proceed
                              </p>
                              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                Progress: {formatAudioTime(audioCurrentTime)} of {formatAudioTime(audioDuration)}
                              </p>
                            </div>
                          ) : (
                            <div className="text-center p-3 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg">
                              <p className="text-sm text-green-700 dark:text-green-300 flex items-center justify-center gap-2">
                                <CheckCircle2 className="w-4 h-4" />
                                Audio completed! You can now proceed.
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Slide (Canvas-based) Viewer */}
                      {currentModule.type === 'slide' && currentModule.content && (
                        <div className="space-y-4">
                          <SlideViewer 
                            content={currentModule.content} 
                            onSlideProgress={(progress) => {
                              if (progress.lastSlideReached) {
                                setSlideLastReached(true);
                              }
                            }}
                          />
                          <div className="text-center p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
                            <p className="text-sm text-blue-700 dark:text-blue-300">
                              📊 Use the arrows or click "Full Screen" to navigate through the slides.
                              {!slideLastReached && (
                                <span className="block mt-1 font-medium">
                                  Navigate to the last slide to enable completion.
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="p-12 text-center">
                    <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
                    <h3 className="font-semibold mb-2">No Modules</h3>
                    <p className="text-muted-foreground">This course has no modules yet.</p>
                  </Card>
                )}
                
                {/* Navigation */}
                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    onClick={() => setActiveModule(Math.max(0, activeModule - 1))}
                    disabled={activeModule === 0}
                  >
                    Previous Module
                  </Button>
                  
                  {activeModule < modules.length - 1 ? (
                    <Button 
                      onClick={markModuleComplete}
                      disabled={!canMarkModuleComplete()}
                      className={!canMarkModuleComplete() ? 'opacity-50' : ''}
                    >
                      {currentModule?.type === 'pdf' && !pdfLastPageReached ? (
                        <>Read all pages to continue</>
                      ) : currentModule?.type === 'ppt' && !pdfLastPageReached ? (
                        <>View all slides to continue</>
                      ) : currentModule?.type === 'audio' && !audioCompleted ? (
                        <>Listen to audio to continue</>
                      ) : (
                        <>Next Module</>
                      )}
                    </Button>
                  ) : quiz ? (
                    <Button 
                      onClick={() => {
                        if (canMarkModuleComplete()) {
                          markModuleComplete();
                        }
                      }} 
                      disabled={!canMarkModuleComplete()}
                      className={`${!canMarkModuleComplete() ? 'opacity-50' : 'bg-green-600 hover:bg-green-700'}`}
                    >
                      <Brain className="w-4 h-4 mr-2" />
                      {currentModule?.type === 'pdf' && !pdfLastPageReached ? (
                        <>Read all pages to take quiz</>
                      ) : currentModule?.type === 'ppt' && !pdfLastPageReached ? (
                        <>View all slides to take quiz</>
                      ) : currentModule?.type === 'slide' && !slideLastReached ? (
                        <>View all slides to take quiz</>
                      ) : currentModule?.type === 'audio' && !audioCompleted ? (
                        <>Listen to audio to take quiz</>
                      ) : (
                        <>Take Quiz (Preview)</>
                      )}
                    </Button>
                  ) : (
                    <Button 
                      onClick={markModuleComplete}
                      disabled={!canMarkModuleComplete()}
                      className={`${canMarkModuleComplete() ? 'bg-green-600 hover:bg-green-700' : 'opacity-50'}`}
                    >
                      {currentModule?.type === 'pdf' && !pdfLastPageReached ? (
                        <>Read all pages to complete</>
                      ) : currentModule?.type === 'ppt' && !pdfLastPageReached ? (
                        <>View all slides to complete</>
                      ) : currentModule?.type === 'slide' && !slideLastReached ? (
                        <>View all slides to complete</>
                      ) : currentModule?.type === 'audio' && !audioCompleted ? (
                        <>Listen to audio to complete</>
                      ) : (
                        <>Mark Complete</>
                      )}
                    </Button>
                  )}
                </div>
              </>
            ) : (
              /* Quiz Section */
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Brain className="w-5 h-5" />
                    {quiz?.title || 'Course Quiz'}
                  </CardTitle>
                  <CardDescription>
                    {quizSubmitted 
                      ? 'Quiz completed (Preview mode - not saved)'
                      : `${quiz?.questions?.length || 0} questions • Passing score: ${quiz?.passing_score || 70}%`
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {quizSubmitted && quizResult ? (
                    <div className="text-center py-8">
                      <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-4 ${
                        quizResult.passed ? 'bg-green-500/10' : 'bg-destructive/10'
                      }`}>
                        {quizResult.passed ? (
                          <CheckCircle2 className="w-12 h-12 text-green-500" />
                        ) : (
                          <XCircle className="w-12 h-12 text-destructive" />
                        )}
                      </div>
                      <h3 className="text-2xl font-bold mb-2">
                        {quizResult.passed ? 'Passed!' : 'Not Passed'}
                      </h3>
                      <p className="text-4xl font-bold text-primary mb-2">{quizResult.score}%</p>
                      <p className="text-muted-foreground mb-4">
                        Passing score: {quizResult.passing_score}%
                      </p>
                      {quizResult.would_earn_points > 0 && (
                        <p className="text-green-600 mb-4">
                          Would earn: +{quizResult.would_earn_points} points
                        </p>
                      )}
                      <p className="text-sm text-amber-600 bg-amber-500/10 p-3 rounded-lg mb-6">
                        <AlertTriangle className="w-4 h-4 inline mr-2" />
                        {quizResult.message}
                      </p>
                      <div className="flex gap-3 justify-center">
                        <Button variant="outline" onClick={resetQuiz}>
                          Try Again
                        </Button>
                        <Button onClick={() => setShowQuiz(false)}>
                          Back to Modules
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <ScrollArea className="h-[500px]">
                      <div className="space-y-6 pr-4">
                        {quiz?.questions?.map((question, qIndex) => (
                          <div key={qIndex} className="p-4 rounded-lg border">
                            <h4 className="font-medium mb-4">
                              {qIndex + 1}. {question.question}
                            </h4>
                            <RadioGroup
                              value={quizAnswers[qIndex]?.toString()}
                              onValueChange={(value) => setQuizAnswers({
                                ...quizAnswers,
                                [qIndex]: parseInt(value)
                              })}
                            >
                              {question.options?.map((option, oIndex) => {
                                // Skip empty/blank options
                                if (!option || option.trim() === '') {
                                  return null;
                                }
                                return (
                                  <div key={oIndex} className="flex items-center space-x-2 py-2">
                                    <RadioGroupItem value={oIndex.toString()} id={`q${qIndex}-o${oIndex}`} />
                                    <Label htmlFor={`q${qIndex}-o${oIndex}`} className="cursor-pointer flex-1">
                                      {option}
                                    </Label>
                                  </div>
                                );
                              })}
                            </RadioGroup>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                  
                  {!quizSubmitted && (
                    <div className="flex gap-3 mt-6">
                      <Button variant="outline" onClick={() => setShowQuiz(false)}>
                        Back to Modules
                      </Button>
                      <Button
                        onClick={handleQuizSubmit}
                        disabled={Object.keys(quizAnswers).length < (quiz?.questions?.length || 0) || submittingQuiz}
                        className="flex-1"
                      >
                        {submittingQuiz ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          'Submit Quiz (Preview)'
                        )}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
          
          {/* Sidebar */}
          <div className="space-y-6">
            {/* Course Progress (simulated) */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Preview Progress</CardTitle>
              </CardHeader>
              <CardContent>
                <Progress value={((activeModule + 1) / Math.max(modules.length, 1)) * 100} className="mb-2" />
                <p className="text-sm text-muted-foreground">
                  Module {activeModule + 1} of {modules.length || 1}
                </p>
              </CardContent>
            </Card>
            
            {/* Module List */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Modules</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[300px]">
                  <div className="p-4 space-y-2">
                    {modules.map((module, index) => (
                      <button
                        key={module.id || index}
                        onClick={() => { setActiveModule(index); setShowQuiz(false); }}
                        className={`w-full text-left p-3 rounded-lg transition-colors ${
                          activeModule === index && !showQuiz
                            ? 'bg-primary text-primary-foreground'
                            : 'hover:bg-muted'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-1.5 rounded ${
                            activeModule === index && !showQuiz ? 'bg-primary-foreground/20' : 'bg-muted'
                          }`}>
                            {getModuleIcon(module.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{module.title}</p>
                            <p className={`text-xs ${
                              activeModule === index && !showQuiz ? 'text-primary-foreground/70' : 'text-muted-foreground'
                            }`}>
                              {module.duration_minutes || 5} min • {module.type}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                    
                    {quiz && (
                      <>
                        <Separator />
                        <button
                          onClick={() => setShowQuiz(true)}
                          className={`w-full text-left p-3 rounded-lg transition-colors ${
                            showQuiz ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`p-1.5 rounded ${showQuiz ? 'bg-primary-foreground/20' : 'bg-muted'}`}>
                              <Brain className="w-4 h-4" />
                            </div>
                            <div>
                              <p className="font-medium">Quiz</p>
                              <p className={`text-xs ${showQuiz ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                                {quiz.questions?.length || 0} questions
                              </p>
                            </div>
                          </div>
                        </button>
                      </>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
            
            {/* Quiz Thumbnail */}
            {quiz?.thumbnail_url && (
              <Card>
                <CardContent className="p-4">
                  <img
                    src={quiz.thumbnail_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${quiz.thumbnail_url}` : quiz.thumbnail_url}
                    alt="Quiz thumbnail"
                    className="w-full h-32 object-cover rounded-lg"
                  />
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CoursePreview;
